"use client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { logNativeIntentEvent } from "./native";
import { trackWebAdEvent } from "./web-advertising";

/**
 * Mid-funnel signals for Meta, derived from product analytics we already fire.
 *
 * Purchases are rare per ad set, and Meta needs ~50 optimisation events a week
 * to leave its learning phase, so it also gets content views and checkout
 * starts. Only these allowlisted events are forwarded, and only an ID, a type
 * and a price ever leave: never names, slugs or free text. Consent is checked
 * downstream (Pixel/SDK), exactly as for PageView.
 */
type Signal = {
  intent: "view_content" | "initiate_checkout" | "add_payment_info";
  contentType: string;
  idKey: string;
};
const SIGNALS: Record<string, Signal> = {
  [ANALYTICS_EVENTS.EVENT_VIEWED]: {
    intent: "view_content",
    contentType: "event",
    idKey: "event_id",
  },
  [ANALYTICS_EVENTS.HIIVE_VIEWED]: {
    intent: "view_content",
    contentType: "hiive",
    idKey: "hiive_id",
  },
  [ANALYTICS_EVENTS.VENUE_VIEWED]: {
    intent: "view_content",
    contentType: "venue",
    idKey: "venue_id",
  },
  [ANALYTICS_EVENTS.VENUE_EXPERIENCE_VIEWED]: {
    intent: "view_content",
    contentType: "venue_experience",
    idKey: "experience_id",
  },
  [ANALYTICS_EVENTS.SHOP_PRODUCT_VIEWED]: {
    intent: "view_content",
    contentType: "shop_item",
    idKey: "shop_item_id",
  },
  [ANALYTICS_EVENTS.PACK_VIEWED]: {
    intent: "view_content",
    contentType: "pack",
    idKey: "pack_id",
  },
  [ANALYTICS_EVENTS.EVENT_BOOKING_STARTED]: {
    intent: "initiate_checkout",
    contentType: "event",
    idKey: "event_id",
  },
  [ANALYTICS_EVENTS.VENUE_BOOKING_PAYMENT_VIEWED]: {
    intent: "initiate_checkout",
    contentType: "venue_booking",
    idKey: "booking_id",
  },
  [ANALYTICS_EVENTS.SHOP_CHECKOUT_STARTED]: {
    intent: "initiate_checkout",
    contentType: "shop_item",
    idKey: "shop_item_id",
  },
  [ANALYTICS_EVENTS.PACK_PURCHASE_STARTED]: {
    intent: "initiate_checkout",
    contentType: "pack",
    idKey: "pack_id",
  },
  // Paying is started: the last step before Purchase, and a much denser
  // optimisation signal than Purchase while ticket volume is low.
  [ANALYTICS_EVENTS.EVENT_BOOKING_PAYMENT_STARTED]: {
    intent: "add_payment_info",
    contentType: "event_booking",
    idKey: "booking_id",
  },
  [ANALYTICS_EVENTS.VENUE_BOOKING_PAYMENT_STARTED]: {
    intent: "add_payment_info",
    contentType: "venue_booking",
    idKey: "booking_id",
  },
  [ANALYTICS_EVENTS.MEMBERSHIP_PAYMENT_STARTED]: {
    intent: "initiate_checkout",
    contentType: "membership",
    idKey: "membership_id",
  },
};
export function adSignalFor(
  event: string,
  properties: Record<string, unknown> = {},
) {
  const signal = SIGNALS[event];
  if (!signal) return null;
  const rawId = properties[signal.idKey];
  if (
    (typeof rawId !== "string" && typeof rawId !== "number") ||
    String(rawId).length === 0 ||
    String(rawId).length > 100
  )
    return null;
  const amount = properties.amount_minor;
  const currency = properties.currency;
  const priced =
    typeof amount === "number" &&
    Number.isFinite(amount) &&
    amount > 0 &&
    typeof currency === "string" &&
    /^[A-Z]{3}$/.test(currency);
  return {
    intent: signal.intent,
    contentType: signal.contentType,
    contentId: `${signal.contentType}:${rawId}`,
    ...(priced ? { amount: Math.round(amount) / 100, currency } : {}),
  };
}
export function forwardAdSignal(
  event: string,
  properties?: Record<string, unknown>,
) {
  try {
    const signal = adSignalFor(event, properties);
    if (!signal) return;
    trackWebAdEvent(
      signal.intent === "view_content"
        ? "ViewContent"
        : signal.intent === "add_payment_info"
          ? "AddPaymentInfo"
          : "InitiateCheckout",
      {
        content_ids: [signal.contentId],
        content_type: signal.contentType,
        ...(signal.amount != null
          ? { value: signal.amount, currency: signal.currency }
          : {}),
      },
    );
    void logNativeIntentEvent({
      event: signal.intent,
      content_type: signal.contentType,
      content_id: signal.contentId,
      amount: signal.amount,
      currency: signal.currency,
    });
  } catch {
    /* Advertising measurement never interrupts product analytics. */
  }
}
